import { POPULATION_TYPE } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import { uniq } from "@/fp";
import ManageTrustModalVue from "@/views/customize/in-motion/exposure-control/manage-trust-modal/ManageTrustModal.vue";
import { EventView } from "./event-view";

/**
 * What the Gen AI row menu can do — the same actions, checks and targets as
 * the app's `ms_event_menu` (components/modals/events-modal/menus.ts), behind
 * gen-ai-ui's menu instead of the app's drill-down popup.
 */
export type RowAction =
  | { type: "investigate_sender" }
  | { type: "investigate_person"; id: string }
  | { type: "investigate_file"; id: string }
  | { type: "manage_trust"; id: string; entity_type: "organization" | "employee" };

const entities = (e: EventView) =>
  [...(e.raw.exposedTo || []), e.raw.exposedBy].filter(Boolean);

/** the people and organisations trust can be managed for */
export const trust_entities = (e: EventView) =>
  entities(e).filter(
    (f: any) =>
      f.type === "organization" || !f.isExternal || (f.isExternal && f.isPersonalAddress)
  );

const event_l0s = (e: EventView): string[] =>
  (e.raw.items || [])
    .filter((item: any) => item && item.labels && item.labels.infoLabel && item.labels.infoLabel.id)
    .map(({ labels: { infoLabel } }: any) => {
      const label = SharedState.allInfoTypes.value[infoLabel.id];
      if (!label) return null;
      if (label.generation == 0) return label.id;
      const l0 = SharedState.get_l0_by_code(label.code);
      return l0 ? l0.id : null;
    })
    .filter(Boolean);

/**
 * As the app decides it: an event that only touches the "Other" category has
 * no information trust to manage, by design.
 */
export const can_manage_trust = (e: EventView) => {
  const l0s = event_l0s(e);
  return (
    trust_entities(e).length > 0 &&
    (l0s.length > 1 || (l0s.length === 1 && l0s[0] != SharedState.default_label_id.value))
  );
};

/** "close" means the caller should close the list: the app has navigated */
export const run_row_action = async (
  e: EventView,
  action: RowAction
): Promise<"close" | "idle"> => {
  const { is_restricted_app, show_restrictions_modal } = SharedState.restrictions;

  if (action.type === "manage_trust") {
    if (is_restricted_app("trust_management")) {
      show_restrictions_modal("app", "Trust Management");
      return "idle";
    }
    const entity = trust_entities(e).find((d: any) => d.id === action.id);
    if (!entity) return "idle";
    await UseModal().show(ManageTrustModalVue, {
      entity: {
        ...entity,
        type:
          action.entity_type === "organization"
            ? POPULATION_TYPE.ORGANIZATION
            : POPULATION_TYPE.EMPLOYEE,
      },
      info_types: uniq(event_l0s(e)),
    });
    return "idle";
  }

  if (is_restricted_app("entity_investigation")) {
    show_restrictions_modal("app", "Entity Investigation");
    return "idle";
  }

  if (action.type === "investigate_sender") {
    const by = e.raw.exposedBy;
    if (!by) return "idle";
    SharedState.investigation.investigate({
      type: POPULATION_TYPE.EMPLOYEE,
      name: by.name,
      email: by.email,
      id: by.id,
    });
    return "close";
  }

  if (action.type === "investigate_person") {
    const person = entities(e).find((r: any) => r.id === action.id);
    if (!person) return "idle";
    const type =
      person.type === "organization"
        ? POPULATION_TYPE.ORGANIZATION
        : person.isExternal
        ? POPULATION_TYPE.CONTACT
        : POPULATION_TYPE.EMPLOYEE;
    SharedState.investigation.investigate({
      type,
      name: person.name,
      email: person.email,
      id: person.id,
    });
    return "close";
  }

  const file = (e.raw.items || []).find((f: any) => f && f.id === action.id);
  if (!file) return "idle";
  SharedState.investigation.investigate({
    type: POPULATION_TYPE.FILE,
    name: file.name,
    id: file.id,
    cluster_id: file.clusterUID,
  });
  return "close";
};
